// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#collection-cadence
import * as React from 'react';
import { CloudAlert, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  isUpstreamImpaired,
  type UpstreamStatus,
  type UpstreamStatusEntry,
} from '@greenlight/shared';

interface UpstreamStatusBannerProps {
  upstream: UpstreamStatus;
}

// Written out rather than composed, so Tailwind's scanner sees whole classes.
const TONE = {
  degraded: {
    container: 'border-[hsl(var(--status-amber))] bg-[hsl(var(--status-amber)/0.15)]',
    icon: 'text-[hsl(var(--status-amber-text))]',
  },
  major: {
    container: 'border-[hsl(var(--status-red))] bg-[hsl(var(--status-red)/0.15)]',
    icon: 'text-[hsl(var(--status-red-text))]',
  },
} as const;

const DISMISS_STORAGE_KEY = 'gl.upstream-banner.dismissed';

/** Identifies the reading so dismissing one incident does not silence the next. */
function dismissKey(entry: UpstreamStatusEntry): string {
  return `${entry.key}:${entry.state}:${entry.description ?? ''}`;
}

/**
 * Dismissal lives in `sessionStorage` so it survives the reload it exists to
 * survive: during an incident the dashboard is reloaded repeatedly, and a
 * banner that returns on every load is one nobody can dismiss. Per-tab and
 * cleared with the browser session, so a later incident is never pre-silenced.
 * Every access is guarded — a private window or blocked site data throws.
 */
function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistDismissed(key: string): void {
  try {
    window.sessionStorage.setItem(DISMISS_STORAGE_KEY, key);
  } catch {
    // Ignored: dismissal degrades to per-render, which is the previous behaviour.
  }
}

function headline(entry: UpstreamStatusEntry): string {
  return entry.signal === 'observed'
    ? `Greenlight cannot reach ${entry.label}.`
    : `${entry.label} is reporting problems.`;
}

/**
 * Dashboard-wide banner while an upstream Greenlight's own critical path runs
 * through is impaired. Every role sees it: a citizen developer staring at a
 * failed registration is the person who most needs to know it was not their
 * doing, and the diagnostics that explain it live on an org-admin page.
 *
 * Dismissable, unlike the fleet-reporting banner. Nothing here is enforcement —
 * it is an explanation, and someone who has read it should be able to get on
 * with their day. The key includes the state and the vendor's own wording, so a
 * dismissal covers this incident rather than every future one.
 */
export function UpstreamStatusBanner({
  upstream,
}: UpstreamStatusBannerProps): React.JSX.Element | null {
  const [dismissed, setDismissed] = React.useState<string | null>(readDismissed);
  const impaired = upstream.find((entry) => isUpstreamImpaired(entry.state));

  if (!impaired || dismissed === dismissKey(impaired)) return null;
  const tone = impaired.state === 'major' ? TONE.major : TONE.degraded;

  return (
    <div
      role="region"
      aria-label="Upstream service status"
      data-testid="upstream-status-banner"
      data-state={impaired.state}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6 ${tone.container}`}
    >
      <CloudAlert className={`size-4 shrink-0 ${tone.icon}`} aria-hidden />
      <p className="min-w-0 flex-1 text-[13px] text-foreground">
        <span className="font-semibold">{headline(impaired)}</span>{' '}
        <span className="text-muted-foreground">
          Registering apps, pipeline runs, and merges may fail until it recovers.
          {impaired.description ? ` ${impaired.label} reports: “${impaired.description}”.` : ''}
        </span>
      </p>
      <Button
        variant="ghost"
        size="sm"
        className="h-11 shrink-0 md:h-9"
        aria-label="Dismiss upstream status notice"
        onClick={() => {
          const key = dismissKey(impaired);
          persistDismissed(key);
          setDismissed(key);
        }}
      >
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  );
}
